import { type ReactNode } from 'react';
import {
	AUTHORIZATION_METHOD_COPY,
	RESEND_COUNTDOWN_KEY,
	RESEND_KEY,
} from '@gov-nx/component/common/Autorizace/methods';
import type { AuthorizationType } from '@gov-nx/component/common/Autorizace/types';
import type { UncheckedKey } from '@gov-nx/core/service/translations/translation.types';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import type { PageCode } from '@gov-nx/module/page/definitions/codes';
import { Button } from '@gov-nx/ui/web/Button/Button';
import { ButtonGroupLayout } from '@gov-nx/ui/web/Layout/ButtonGroupLayout';
import { LayoutGap } from '@gov-nx/ui/web/Layout/LayoutGap';
import { MarkdownRender } from '../../Markdown/MarkdownRender';

interface MethodPanelProps {
	code: (typeof PageCode)['autorizace'];
	type: AuthorizationType;
	/** Deeplink injected into subtitles that contain the Identity portal link. */
	identityUrl: string;
	/** A code has already been requested for this method. */
	codeSent: boolean;
	/** The "send code" request is in flight. */
	sending: boolean;
	/**
	 * Remaining seconds on the resend cooldown for this method (0 = not
	 * throttled). Owned by {@link AuthorizationMethods} so it persists per method
	 * across this panel unmounting on a method switch.
	 */
	resendSecondsLeft: number;
	/** The "confirm code" request is in flight. */
	submitting: boolean;
	/** Pre-built one-time-password input (owned by the parent form). */
	codeInput: ReactNode;
	/** Send the code, or — for the eID method — redirect to the login page. */
	onAction: () => void;
	/** Re-request a code. */
	onResend: () => void;
}

/**
 * Inline content rendered directly under the selected authorization radio
 * option. Replaces the former wizard Step 2: instead of advancing to a separate
 * step, the option expands in place to show the subtitle, the action button
 * and — once a code has been sent — the code input with confirm / resend.
 */
export const MethodPanel = ({
	code,
	type,
	identityUrl,
	codeSent,
	sending,
	resendSecondsLeft,
	submitting,
	codeInput,
	onAction,
	onResend,
}: MethodPanelProps) => {
	const { t } = useLocale(code);
	const copy = AUTHORIZATION_METHOD_COPY[type];
	const isResendOnCooldown = resendSecondsLeft > 0;

	const subtitle = (key: string) => (
		<MarkdownRender
			disableWrapper
			applyClasses={{
				paragraph: 'text-body-s! text-(--text-primary)!',
			}}>
			{t<UncheckedKey>(key, copy.hasIdentityLink ? { identityUrl } : undefined)}
		</MarkdownRender>
	);

	// Indent to line up under the radio label text. The size-m radio reserves
	// 1.25rem (circle) + 0.75rem (gap) = 2rem before the label, which equals the
	// global --spacing-xl token (the radio's own --height-m-nudge/--spacing-s-nudge
	// vars are component-scoped and resolve to 0 here).
	return (
		<div className="ml-(--spacing-xl) mt-(--spacing-s) mb-(--spacing-m)">
			<LayoutGap gap="m">
				{copy.isRedirect ? (
					<>
						{subtitle(copy.subtitleKey)}
						<ButtonGroupLayout align="left">
							<Button
								nativeType="button"
								onClick={onAction}
								isDisabled={submitting}
								color="primary"
								size="l"
								type="solid">
								{t<UncheckedKey>(copy.actionKey)}
							</Button>
						</ButtonGroupLayout>
					</>
				) : !codeSent ? (
					<>
						{subtitle(copy.subtitleKey)}
						<ButtonGroupLayout align="left">
							<Button
								nativeType="button"
								onClick={onAction}
								isLoading={sending}
								isDisabled={submitting}
								color="primary"
								size="l"
								type="solid">
								{t<UncheckedKey>(copy.actionKey)}
							</Button>
						</ButtonGroupLayout>
					</>
				) : (
					// The subtitle sits flush against the input (0 gap), with a 10px
					// gap before the action buttons — per design. Wrapping in its own
					// flex column opts out of the parent LayoutGap's uniform spacing.
					<div className="flex flex-col">
						{copy.subtitleSentKey && subtitle(copy.subtitleSentKey)}
						{codeInput}
						<ButtonGroupLayout
							align="left"
							customClasses="mt-2.5!">
							<Button
								nativeType="submit"
								isLoading={submitting}
								color="primary"
								size="l"
								type="solid">
								{copy.confirmKey && t<UncheckedKey>(copy.confirmKey)}
							</Button>
							<Button
								nativeType="button"
								onClick={onResend}
								isLoading={sending}
								isDisabled={submitting || isResendOnCooldown}
								color="primary"
								size="l"
								type="outlined">
								{isResendOnCooldown
									? t(RESEND_COUNTDOWN_KEY, {
											seconds: resendSecondsLeft,
										})
									: t(RESEND_KEY)}
							</Button>
						</ButtonGroupLayout>
					</div>
				)}
			</LayoutGap>
		</div>
	);
};
